import { ref } from "vue";
import {
  ChatbotProtocol,
  ChatbotProtocolError,
  createId,
  stripHtml
} from "./protocol";
import type { ChatbotConfig, ChatClient, TranscriptEntry } from "./types";

const MAX_TRANSCRIPT_ENTRIES = 120;
const FALLBACK_EMPTY = "Não recebi uma resposta do chatbot agora. Tente novamente em alguns instantes.";
const FALLBACK_SEND = "Não consegui enviar a mensagem agora. Tente novamente em alguns instantes.";
const FALLBACK_TIMEOUT = "A resposta demorou mais do que o esperado. Tente novamente em alguns instantes.";

/**
 * Coordinates the visitor transcript, REST calls, and visible failure states
 * for the floating chatbot. Page-session only — no history across reloads.
 */
export const useChatClient = (config: ChatbotConfig): ChatClient => {
  const tpl = config.templates ?? {};
  const emptyMessage = tpl.cwErrorEmpty || FALLBACK_EMPTY;
  const sendFailure = tpl.cwErrorSend || FALLBACK_SEND;
  const timeout = tpl.cwErrorTimeout || FALLBACK_TIMEOUT;

  const sessionId = createId();
  const protocol = new ChatbotProtocol(config.restUrl, config.nonce, sessionId);
  const transcript = ref<TranscriptEntry[]>([]);
  const pending = ref(false);
  const error = ref("");

  const append = (entry: TranscriptEntry): void => {
    const next = [...transcript.value.filter((item) => item.id !== entry.id), entry];
    transcript.value = next.slice(-MAX_TRANSCRIPT_ENTRIES);
  };

  const appendIncomingNotice = (text: string): void => {
    append({
      id: createId(),
      direction: "incoming",
      text,
      at: new Date().toISOString(),
      pending: false
    });
  };

  const markSent = (outgoingId: string): void => {
    transcript.value = transcript.value.map((entry) =>
      entry.id === outgoingId ? { ...entry, pending: false } : entry
    );
  };

  const connect = async (): Promise<void> => {
    // Page-session only — no history restore across reloads.
  };

  const sendMessage = async (text: string): Promise<boolean> => {
    const safeText = stripHtml(text);
    if (!safeText) {
      return false;
    }

    const outgoingId = createId();
    append({
      id: outgoingId,
      direction: "outgoing",
      text: safeText,
      at: new Date().toISOString(),
      pending: true
    });

    pending.value = true;
    error.value = "";

    try {
      const response = await protocol.sendMessage(safeText);
      markSent(outgoingId);

      if (response?.messages?.length) {
        response.messages.forEach(append);
        return true;
      }

      error.value = emptyMessage;
      appendIncomingNotice(emptyMessage);
      return false;
    } catch (caught) {
      const message =
        caught instanceof ChatbotProtocolError && caught.code === "timeout"
          ? timeout
          : sendFailure;
      error.value = message;
      markSent(outgoingId);
      appendIncomingNotice(message);
      return false;
    } finally {
      pending.value = false;
    }
  };

  const clearHistory = async (): Promise<void> => {
    transcript.value = [];
    await protocol.clearHistory();
  };

  return {
    transcript,
    pending,
    error,
    connect,
    sendMessage,
    clearHistory
  };
};
